import styled from "@emotion/styled";
import { css } from "@emotion/react";

// ---------------------------------------------------------------------------
// Global styles (lp3 — has extra CSS vars vs lp1/lp2)
// ---------------------------------------------------------------------------

export const globalStyles = css`
  :root {
    --lp-bg: rgb(240, 243, 253);
    --lp-accent: rgb(125, 64, 255);
    --lp-content-primary: #1a192d;
    --lp-content-secondary: rgba(0, 0, 0, 0.6);
    --lp-sun-30: rgb(255, 238, 128);
    --lp-sun-40: rgb(255, 214, 51);
    --lp-sun-80: rgb(103, 51, 5);
    --lp-border: 1px solid rgba(0, 41, 41, 0.1);
    --lp-shadow: 0px 0px 10px 0px rgba(0, 41, 41, 0.05);
    --lp-card-radius: 32px;
    --lp-divider-color: #e0cfaf;
    --lp-color-orange: #fc8937;
    --lp-color-pink: #f76478;
    --lp-color-purple: #a272f7;
    --lp-paragraph-color: rgba(0, 0, 0, 0.7);
    --lp-video-overlay: linear-gradient(to bottom, rgba(0, 0, 0, 0%), rgba(0, 0, 0, 0%), rgba(0, 0, 0, 40%));
    --lp-quote-gradient: linear-gradient(to right, var(--lp-sun-40), var(--lp-sun-30));
  }
`;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export const PageWrapper = styled("div")`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--lp-bg);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

export const Content = styled("div")`
  max-width: 1024px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 16px 0 32px;
`;

export const WhiteBackground = styled("div")`
  background-color: #ffffff;
  box-shadow: var(--lp-shadow);
  width: 100%;
  display: flex;
  justify-content: center;
  border-radius: 60px 60px 0 0;
`;

export const Divider = styled("div")`
  height: 2px;
  background-color: var(--lp-divider-color);
  opacity: 0.25;

  @media screen and (max-width: 1048px) {
    margin: 0 18px;
  }
`;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export const HeaderWrapper = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  border: var(--lp-border);
  padding: 9px 15px;
  border-radius: 9px;
  position: sticky;
  top: 0;
  width: calc(1024px + 36px);
  max-width: calc(100% - 18px);
  z-index: 10;
  box-shadow: var(--lp-shadow);
  margin: 18px auto 0;

  @media screen and (max-width: 1048px) {
    max-width: calc(100% - 24px);
  }
`;

export const LogoLink = styled("a")`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1a192d;
  text-decoration: none;
`;

export const LogoTitle = styled("h3")`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
`;

export const HeaderRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const HeaderButton = styled("a")<{ variant?: "dark" | "login" }>`
  border-radius: 32px;
  line-height: 32px;
  padding: 0 16px;
  border: 1px solid ${(p) => (p.variant === "dark" ? "#7d40ff" : "rgba(0, 41, 41, 0.1)")};
  background-color: ${(p) => (p.variant === "dark" ? "#7d40ff" : "#ffffff")};
  font-weight: 600;
  cursor: pointer;
  color: ${(p) => (p.variant === "dark" ? "#ffffff" : "#1a192d")};
  text-decoration: none;
  font-size: 14px;

  &:hover {
    ${(p) => (p.variant === "dark" ? "opacity: 0.85;" : "background-color: #f5f5f5;")}
  }

  @media screen and (max-width: 1048px) {
    ${(p) => (p.variant !== "dark" ? "display: none;" : "")}
  }

  @media screen and (max-width: 400px) {
    ${(p) => (p.variant === "dark" ? "font-size: 14px; padding: 0 10px;" : "")}
  }
`;

export const Hamburger = styled("button")`
  border: none;
  background: transparent;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 4px;

  svg {
    fill: #1a192d;
    width: 24px;
    height: 24px;
  }

  @media screen and (max-width: 1048px) {
    display: flex;
  }
`;

// ---------------------------------------------------------------------------
// Hero (two-column)
// ---------------------------------------------------------------------------

export const HeroSection = styled("div")`
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 8px 0 0;
  position: relative;
`;

export const HeroSplit = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 48px;

  @media screen and (max-width: 1048px) {
    flex-direction: column;
    padding: 0 18px;
    gap: 18px;
  }
`;

export const HeroLeft = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 39px;

  h1 {
    margin: 0;
    font-size: clamp(1.75rem, 7.5vw, 3rem);
    line-height: 1.1;
    max-width: 480px;
    font-weight: 800;
    color: var(--lp-content-primary);
  }

  p {
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--lp-paragraph-color);
  }

  @media screen and (max-width: 1048px) {
    gap: 18px;

    h1 {
      font-weight: 700;
      max-width: none;
    }

    p {
      font-size: 1rem;
    }
  }
`;

export const HeroBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media screen and (max-width: 1048px) {
    gap: 15px;
  }
`;

export const HeroCollage = styled("img")`
  flex-shrink: 0;
  width: 452px;
  height: 471px;
  aspect-ratio: 452 / 471;

  @media screen and (max-width: 1048px) {
    height: auto;
    width: 100%;
    margin: 0 -9px;
  }
`;

export const RainbowButtonDiv = styled("div")`
  color: #ffffff;
  line-height: 48px;
  border-radius: 24px;
  padding: 0 6px 0 24px;
  background: var(--lp-accent);
  border: none;
  display: flex;
  width: fit-content;
  font-weight: 500;
  font-size: 1rem;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  text-decoration: none;
`;

export const RainbowButton = styled("a")`
  color: #ffffff;
  line-height: 48px;
  border-radius: 24px;
  padding: 0 6px 0 24px;
  background: var(--lp-accent);
  border: none;
  display: flex;
  width: fit-content;
  font-weight: 500;
  font-size: 1rem;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  text-decoration: none;
  cursor: pointer;
`;

export const RainbowArrow = styled("div")`
  background-color: #ffffff;
  color: var(--lp-accent);
  width: 36px;
  height: 36px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DesktopOnly = styled("div")`
  display: contents;
  @media screen and (max-width: 1048px) {
    display: none;
  }
`;

export const MobileOnly = styled("div")`
  display: none;
  @media screen and (max-width: 1048px) {
    display: contents;
  }
`;

// ---------------------------------------------------------------------------
// Review section
// ---------------------------------------------------------------------------

export const ReviewSection = styled("section")`
  display: flex;
  flex-direction: column;
  gap: 9px;

  @media screen and (max-width: 1048px) {
    gap: 6px;
  }
`;

export const ReviewRow = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 9px;

  @media screen and (max-width: 1048px) {
    flex-direction: column;
  }
`;

export const Stars = styled("div")`
  display: flex;
  gap: 6px;
  align-items: center;
`;

export const QuoteHighlight = styled("span")`
  position: relative;

  &::before {
    content: "";
    background: var(--lp-quote-gradient);
    border-radius: 4px;
    position: absolute;
    inset: 0;
    opacity: 0.66;
  }
`;

export const QuoteText = styled("span")`
  color: var(--lp-sun-80);
  padding: 1px 9px;
  font-weight: 600;
  position: relative;
  z-index: 1;
`;

export const ReviewCta = styled("a")`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.7;
  color: var(--lp-content-primary);
  text-decoration: none;
`;

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

export const FeaturesGrid = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 96px;

  @media screen and (max-width: 1048px) {
    padding: 0 18px;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
`;

export const Feature = styled("div")<{ color: string }>`
  display: flex;
  flex-direction: column;
  gap: 18px;

  h2 {
    margin: 0;
    display: flex;
    flex-direction: row;
    gap: 9px;
    align-items: center;
    font-size: 1.125rem;
    font-weight: 700;
    color: ${(p) => p.color};
  }

  p {
    font-size: 0.875rem;
    color: var(--lp-paragraph-color);
  }
`;

export const FeatureIcon = styled("div")<{ bg: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: #ffffff;
  background-color: ${(p) => p.bg};
  flex-shrink: 0;
`;

// ---------------------------------------------------------------------------
// Change (Video Testimonials)
// ---------------------------------------------------------------------------

export const ChangeWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;

  @media screen and (max-width: 1048px) {
    gap: 18px;
    padding: 0 18px;
  }
`;

export const ChangeHeader = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 9px;
  text-align: center;
  max-width: 550px;

  h2 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 600;
  }

  p {
    font-size: 0.875rem;
    text-align: center;
    color: var(--lp-paragraph-color);
  }

  p b {
    font-weight: 500;
    color: #000000;
  }

  @media screen and (max-width: 1048px) {
    h2 {
      font-size: 1.5rem;
    }
  }
`;

export const VideoGrid = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 18px;
  width: 100%;

  @media screen and (max-width: 1048px) {
    display: none;
  }
`;

export const VideoCell = styled("div")`
  width: 320px;
  height: 280px;
`;

export const VideoWrapper = styled("div")`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  transition: transform 150ms ease-out;
  cursor: pointer;
  width: 100%;
  height: 100%;

  &:hover {
    transform: scale(1.05);
  }

  @media screen and (max-width: 1048px) {
    &:hover {
      transform: none;
    }
  }
`;

export const VideoElement = styled("video")`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const VideoOverlay = styled("div")`
  position: absolute;
  inset: 0;
  background: var(--lp-video-overlay);
  border-radius: 24px;
  pointer-events: none;
`;

export const VideoQuote = styled("h3")<{ isTapLabel?: boolean }>`
  position: absolute;
  bottom: 15px;
  left: ${(p) => (p.isTapLabel ? "50%" : "15px")};
  ${(p) => (p.isTapLabel ? "transform: translateX(-50%);" : "")}
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.2rem;
  color: #ffffff;
  font-weight: 600;

  ${(p) =>
    p.isTapLabel
      ? `
    display: none;
    opacity: 0.65;

    @media screen and (max-width: 1048px) {
      display: block;
    }
  `
      : `
    @media screen and (max-width: 1048px) {
      display: none;
    }
  `}
`;

export const VideoIcon = styled("div")<{ active?: boolean }>`
  position: absolute;
  color: #ffffff;
  right: 15px;
  bottom: 15px;
  opacity: ${(p) => (p.active ? 1 : 0.65)};

  ${VideoWrapper}:hover & {
    opacity: 1;
  }

  @media screen and (max-width: 1048px) {
    ${VideoWrapper}:hover &:not([data-active="true"]) {
      opacity: 0.5;
    }
  }
`;

export const VideoFullButton = styled("button")`
  position: absolute;
  inset: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

// Mobile carousel

export const CarouselPlaceholder = styled("div")`
  display: none;
  @media screen and (max-width: 1048px) {
    display: block;
    aspect-ratio: 320 / 310;
    width: 100%;
  }
`;

export const CarouselContainer = styled("div")`
  overflow: hidden;
  width: 100vw;
  margin: 0 -18px;
  padding: 0 18px;
  position: relative;
  display: none;

  @media screen and (max-width: 1048px) {
    display: block;
  }
`;

export const CarouselTrack = styled("div")<{ offset: number }>`
  display: flex;
  transition: transform 350ms ease-out;
  transform: translateX(calc(${(p) => -p.offset} * (100vw - 39px)));
`;

export const CarouselSlide = styled("div")<{ shrink: boolean }>`
  width: calc(100vw - 39px);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 350ms ease-out;
  transform: ${(p) => (p.shrink ? "scale(0.95)" : "scale(1)")};
`;

export const DotWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 18px;
`;

export const Dot = styled("button")<{ active: boolean }>`
  border: none;
  height: 9px;
  width: 9px;
  padding: 0;
  background-color: #000000;
  border-radius: 50%;
  opacity: ${(p) => (p.active ? 0.75 : 0.25)};
  cursor: pointer;
`;

// ---------------------------------------------------------------------------
// Cards Section
// ---------------------------------------------------------------------------

export const CardsStack = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const CardWrapper = styled("div")<{ reverse?: boolean }>`
  background-color: #ffffff;
  box-shadow: var(--lp-shadow);
  border-radius: var(--lp-card-radius);
  border: var(--lp-border);
  padding: 12px;
  display: flex;
  flex-direction: ${(p) => (p.reverse ? "row-reverse" : "row")};
  align-items: center;

  @media screen and (max-width: 1048px) {
    flex-direction: column-reverse;
  }
`;

export const CardContent = styled("div")`
  padding: 36px;
  display: flex;
  flex-direction: column;
  gap: 39px;

  @media screen and (max-width: 1048px) {
    padding: 18px 0;
  }
`;

export const CardHeader = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 9px;

  h3 {
    font-size: 1.75rem;
    margin: 0;
    font-weight: 600;
  }

  p {
    font-size: 1rem;
    color: var(--lp-paragraph-color);
  }

  @media screen and (max-width: 1048px) {
    h3 {
      font-size: 1.5rem;
    }
  }
`;

export const CardImage = styled("img")`
  width: 480px;
  height: 375px;
  aspect-ratio: 480 / 375;
  flex-shrink: 0;

  @media screen and (max-width: 1048px) {
    width: 100%;
    height: auto;
  }
`;

export const CardQuoteRow = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 18px;
  align-items: center;
`;

export const CardAvatar = styled("img")`
  background: rgba(0, 0, 0, 0.05);
  width: 64px;
  height: 64px;
  border-radius: 12px;
`;

export const CardQuoteContent = styled("div")`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  width: fit-content;
`;

export const CardQuoteHighlight = styled("div")`
  position: relative;

  &::before {
    content: "";
    background: var(--lp-quote-gradient);
    border-radius: 4px;
    position: absolute;
    inset: 0;
    opacity: 0.2;
  }
`;

export const CardQuoteText = styled("div")`
  font-size: 0.875rem;
  color: var(--lp-sun-80);
  padding: 3px 9px;
  font-weight: 600;
  position: relative;
  z-index: 1;
`;

export const CardCta = styled("a")`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.7;
  color: var(--lp-content-primary);
  text-decoration: none;

  @media screen and (max-width: 1048px) {
    display: none;
  }
`;

// ---------------------------------------------------------------------------
// Card Row Section
// ---------------------------------------------------------------------------

export const CardRowGrid = styled("div")`
  padding: 12px 0;
  display: grid;
  grid-template-columns: 1.75fr 1fr 1fr;
  gap: 24px;

  @media screen and (max-width: 1048px) {
    display: flex;
    flex-direction: column;
    padding: 18px;
    padding-top: 0;
  }
`;

export const CardRowItem = styled("a")`
  color: var(--lp-content-primary);
  text-decoration: none;
  background: #ffffff;
  box-shadow: var(--lp-shadow);
  border: var(--lp-border);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
  border-radius: var(--lp-card-radius);
  padding: 32px;
  transition: transform 150ms ease-out;

  &:hover {
    transform: scale(1.05);
  }

  h3 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
  }

  p {
    font-size: 0.875rem;
    color: var(--lp-paragraph-color);
    flex-grow: 1;
  }
`;

export const CardRowBody = styled("div")`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 9px;
  height: 100%;
`;

export const CardRowIconWrapper = styled("div")`
  flex-shrink: 0;
  color: var(--lp-accent);
`;

// ---------------------------------------------------------------------------
// Browse Tutors
// ---------------------------------------------------------------------------

export const BrowseWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 30px;
  background-color: #e8f4fd;
  border-radius: 12px;

  @media screen and (max-width: 1048px) {
    padding: 24px;
    margin: 0 18px;
  }
`;

export const BrowseHeader = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
`;

export const BrowseIconWrapper = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  color: var(--lp-content-primary);
`;

export const BrowseTitle = styled("h2")`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--lp-content-primary);
`;

export const BrowseLinks = styled("div")`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
`;

export const BrowsePill = styled("a")`
  display: flex;
  align-items: center;
  padding: 12px 18px;
  background: #ffffff;
  border: var(--lp-border);
  border-radius: 8px;
  box-shadow: var(--lp-shadow);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--lp-content-primary);
  text-decoration: none;
  transition: transform 150ms ease-out;

  &:hover {
    transform: scale(1.03);
  }
`;

// ---------------------------------------------------------------------------
// Breadcrumb Footer
// ---------------------------------------------------------------------------

export const BreadcrumbFooter = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 9px;
  align-items: center;
  margin-top: 18px;
`;

export const BreadcrumbPill = styled("div")`
  display: flex;
  align-items: center;
  gap: 9px;
  border: var(--lp-border);
  border-radius: 32px;
  padding: 9px 18px;
`;

export const BreadcrumbLink = styled("a")`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--lp-content-primary);
  opacity: 0.4;
  text-decoration: none;
`;

// ---------------------------------------------------------------------------
// Legal Footer
// ---------------------------------------------------------------------------

export const LegalFooter = styled("footer")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  padding: 24px 18px;
  width: 100%;
`;

export const FooterCopyright = styled("span")`
  font-size: 0.75rem;
  color: var(--lp-content-secondary);
  opacity: 0.7;
`;

export const FooterLinks = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
`;

export const FooterLink = styled("a")`
  font-size: 0.75rem;
  color: var(--lp-content-secondary);
  opacity: 0.7;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

export const FooterSep = styled("span")`
  font-size: 0.75rem;
  color: var(--lp-content-secondary);
  opacity: 0.4;
`;

export const CookieButton = styled("button")`
  &#ot-sdk-btn.ot-sdk-show-settings {
    font-size: 0.75rem !important;
    color: var(--lp-content-secondary) !important;
    opacity: 0.7;
    text-decoration: none !important;
    background: none !important;
    background-color: transparent !important;
    border: none !important;
    cursor: pointer !important;
    padding: 0 !important;
    font-family: inherit !important;
    font-weight: normal !important;

    &:hover {
      background-color: transparent !important;
      opacity: 1;
    }
  }
`;
